import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  LayoutTemplate, Layers, ImagePlus, FileText, MessageCircleQuestion, BookOpen, Palette,
  Navigation, GripVertical, Eye, EyeOff, Pencil, Trash2, Sparkles, Upload, RotateCcw, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtDate, imgUrl } from "@/lib/api";
import { PageHeader, EmptyState, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SECTION_DEFS, AI_PROMPT_SUGGESTIONS, FONT_OPTIONS } from "@/storefront/templates";

const TPL_META = [
  ["modern_luxury", "01", "Modern Luxury", ["#FAF9F6", "#1A1A1A", "#C5A059"], "Serif editorial minimalism"],
  ["color_pop", "02", "Color Pop", ["#FFF9E6", "#FF3366", "#00E5FF"], "Bold, vibrant, playful energy"],
  ["editorial_lifestyle", "03", "Editorial Lifestyle", ["#F7F4EF", "#2C2A29", "#8C6D58"], "Magazine storytelling"],
  ["trendy_fashion", "04", "Trendy Fashion", ["#0D0D0D", "#E11D48", "#FFFFFF"], "Lookbook streetwear"],
  ["tech_futuristic", "05", "Tech Futuristic", ["#0A0D14", "#00F0FF", "#00FF66"], "Neon glassmorphism"],
  ["natural_organic", "06", "Natural Organic", ["#F4F7F2", "#2D5A27", "#C86D51"], "Botanical wellness"],
  ["fun_playful", "07", "Fun & Playful", ["#FFFDF5", "#FF6B6B", "#4ECDC4"], "Family joy & color"],
];
const HERO_LAYOUTS = ["split-editorial", "bento", "magazine", "fullbleed", "hud", "arch", "storybook"];
const COLOR_KEYS = [["primary", "Primary"], ["secondary", "Secondary"], ["accent", "Accent"], ["button", "Button"], ["background", "Background"], ["text", "Text"]];
const AI_CONTENT_TYPES = [["about", "About Us"], ["contact", "Contact intro"], ["shipping", "Shipping policy"], ["returns", "Returns & refunds"],
  ["privacy", "Privacy policy"], ["terms", "Terms & conditions"], ["cancellation", "Cancellation policy"], ["tagline", "Tagline"], ["description", "Store description"], ["seo", "SEO metadata"]];

// ---------- Poll-based AI generation (long generations outlive the edge proxy timeout) ----------
function useAiJob() {
  const [jobId, setJobId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!jobId) return;
    setElapsed(0);
    const tick = setInterval(() => setElapsed((s) => s + 1), 1000);
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/agent/store/ai-jobs/${jobId}`);
        if (data.status === "done") { setResult(data.result); setJobId(null); }
        else if (data.status === "error") { setError(data.error || "AI generation failed"); setJobId(null); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => { clearInterval(t); clearInterval(tick); };
  }, [jobId]);
  const start = async (kind, params) => {
    setResult(null); setError("");
    const { data } = await api.post("/agent/store/ai-jobs", { kind, params });
    setJobId(data.job_id);
  };
  return { start, running: !!jobId, elapsed, result, error, clear: () => { setResult(null); setError(""); } };
}

// ---------- Image picker (upload / AI / library) ----------
function ImagePicker({ sectionType, current, onPick }) {
  const qc = useQueryClient();
  const fileRef = useRef();
  const [prompt, setPrompt] = useState("");
  const [uploads, setUploads] = useState([]); // [{id, name, pct, failed}]
  const aiJob = useAiJob();
  useEffect(() => {
    if (aiJob.result?.file_url) {
      toast.success("Image generated");
      qc.invalidateQueries({ queryKey: ["store-assets"] });
      onPick(aiJob.result.file_url);
      aiJob.clear();
    }
  }, [aiJob.result]);
  useEffect(() => { if (aiJob.error) { toast.error(aiJob.error); aiJob.clear(); } }, [aiJob.error]);
  const { data: assets } = useQuery({
    queryKey: ["store-assets", sectionType],
    queryFn: async () => (await api.get(`/agent/store/assets${sectionType ? `?section_type=${sectionType}` : ""}`)).data,
  });
  const upload = async (file, autoPick = false) => {
    const okTypes = ["image/jpeg", "image/png", "image/webp"];
    if (!okTypes.includes(file.type)) {
      toast.error(`"${file.name}" is ${file.type || "unknown type"} — allowed: JPG, PNG, WEBP (max 5 MB each)`);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" is ${(file.size / 1048576).toFixed(1)} MB — images max 5 MB`);
      return;
    }
    const upId = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    setUploads((prev) => [...prev, { id: upId, name: file.name, pct: 0, failed: false }]);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/agent/store/assets/upload?section_type=${sectionType || "custom"}`, fd, {
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploads((prev) => prev.map((u) => (u.id === upId ? { ...u, pct } : u)));
        },
      });
      qc.invalidateQueries({ queryKey: ["store-assets"] });
      if (autoPick) onPick(data.file_url);
      toast.success(`"${file.name}" added to library`);
      setUploads((prev) => prev.filter((u) => u.id !== upId));
    } catch (e) {
      setUploads((prev) => prev.map((u) => (u.id === upId ? { ...u, failed: true } : u)));
      toast.error(errMsg(e));
    }
  };
  const uploadMany = (files) => {
    const list = Array.from(files || []);
    if (list.length > 5) toast.error(`You selected ${list.length} files — up to 5 images at a time (first 5 used)`);
    list.slice(0, 5).forEach((f, i) => upload(f, i === 0));
  };
  const generate = async () => {
    if (!prompt.trim()) return;
    try { await aiJob.start("image", { section_type: sectionType || "custom", prompt }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid={`image-picker-${sectionType}`} className="rounded-lg border border-slate-200 p-3">
      {current && <img src={imgUrl(current)} alt="" className="mb-2 h-24 w-full rounded-lg object-cover" data-testid="current-image" />}
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" data-testid="upload-image-btn" onClick={() => fileRef.current.click()} className="border-slate-300 text-slate-600">
          <Upload size={11} /> Upload images
        </Button>
        <input ref={fileRef} type="file" multiple accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" className="hidden" data-testid="image-file-input"
          onChange={(e) => { uploadMany(e.target.files); e.target.value = ""; }} />
        {current && <Button size="sm" variant="outline" data-testid="remove-image-btn" onClick={() => onPick("")} className="border-rose-200 text-rose-500"><Trash2 size={11} /> Remove</Button>}
      </div>
      <p className="mt-1 text-[9px] text-slate-400">JPG, PNG or WEBP · max 5 MB each · up to 5 at once · first image is applied here, all are saved to your library.</p>
      {uploads.length > 0 && (
        <div className="mt-2 space-y-1.5" data-testid="upload-progress-list">
          {uploads.map((u) => (
            <div key={u.id} data-testid={`upload-progress-${u.id}`} className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5">
              <div className="flex items-center justify-between text-[10px]">
                <span className="max-w-[70%] truncate text-slate-500">{u.name}</span>
                <span className={`font-mono font-semibold ${u.failed ? "text-rose-500" : "text-emerald-600"}`}>{u.failed ? "failed" : `${u.pct}%`}</span>
              </div>
              <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-200">
                <div className={`h-full rounded-full transition-[width] duration-200 ${u.failed ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${u.failed ? 100 : u.pct}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 border-t border-slate-100 pt-3">
        <p className="mb-1.5 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-violet-600"><Sparkles size={10} /> Create with AI</p>
        <div className="flex gap-2">
          <Input data-testid="ai-image-prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe the image…" className="border-slate-200 text-xs" />
          <Button size="sm" data-testid="ai-image-generate-btn" onClick={generate} disabled={aiJob.running || !prompt.trim()}
            className="shrink-0 bg-violet-600 text-white hover:bg-violet-500">{aiJob.running ? "Generating…" : "Generate"}</Button>
        </div>
        {aiJob.running && (
          <div data-testid="ai-image-progress" className="mt-2 rounded-lg border border-violet-200 bg-violet-50 px-2.5 py-2">
            <div className="flex items-center justify-between text-[10px]">
              <span className="flex items-center gap-1.5 font-semibold text-violet-700">
                <Sparkles size={10} className="animate-pulse" /> AI is creating your image…
              </span>
              <span className="font-mono text-violet-500" data-testid="ai-image-elapsed">{aiJob.elapsed}s</span>
            </div>
            <div className="relative mt-1.5 h-1.5 overflow-hidden rounded-full bg-violet-100">
              <div className="absolute inset-y-0 w-1/3 animate-[aiSlide_1.2s_ease-in-out_infinite] rounded-full bg-violet-500" />
            </div>
            <p className="mt-1 text-[9px] text-violet-400">Usually takes 10–30 seconds — the image lands in your library automatically.</p>
          </div>
        )}
        {aiJob.error && (
          <p data-testid="ai-image-error" className="mt-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[10px] font-medium text-rose-600">
            {aiJob.error} — try again or upload an image instead.
          </p>
        )}
        <style>{`@keyframes aiSlide { 0% { left: -33%; } 100% { left: 100%; } }`}</style>
        <div className="mt-2 flex flex-wrap gap-1">
          {AI_PROMPT_SUGGESTIONS.map((s) => (
            <button key={s} onClick={() => setPrompt(s)} className="rounded-full border border-slate-200 px-2 py-0.5 text-[9px] text-slate-500 hover:border-violet-400">{s}</button>
          ))}
        </div>
      </div>
      {(assets?.items?.length > 0) && (
        <div className="mt-3 grid grid-cols-4 gap-1.5 border-t border-slate-100 pt-3">
          {assets.items.slice(0, 8).map((a) => (
            <button key={a.id} data-testid={`asset-${a.id}`} onClick={() => onPick(a.file_url)} className="overflow-hidden rounded-md border border-slate-200 hover:border-[#FF6B00]">
              <img src={imgUrl(a.file_url)} alt={a.alt_text} className="h-14 w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Section editor dialog ----------
function SectionEditor({ section, onClose, onSaved }) {
  const def = SECTION_DEFS.find((d) => d.type === section?.section_type);
  const [content, setContent] = useState(section?.content || {});
  const save = useMutation({
    mutationFn: async () => api.put(`/agent/store/sections/${section.section_type}`, { content }),
    onSuccess: () => { toast.success("Section saved"); onSaved(); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!section) return null;
  return (
    <Dialog open={!!section} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
        <DialogHeader><DialogTitle>Edit — {def?.label}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {(def?.fields || []).map((f) => f === "image" ? (
            <div key={f}>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Image</p>
              <ImagePicker sectionType={section.section_type} current={content.image} onPick={(url) => setContent({ ...content, image: url })} />
            </div>
          ) : (
            <div key={f}>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{f.replace(/_/g, " ")}</p>
              {f === "text" || f === "subheading" ? (
                <textarea data-testid={`field-${f}`} rows={3} value={content[f] || ""} onChange={(e) => setContent({ ...content, [f]: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-[#FF6B00]" />
              ) : (
                <Input data-testid={`field-${f}`} value={content[f] || ""} onChange={(e) => setContent({ ...content, [f]: e.target.value })} className="border-slate-200 text-xs" />
              )}
            </div>
          ))}
          {section.section_type === "hero" && (
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Hero layout</p>
              <select data-testid="hero-layout-select" value={content.layout || ""} onChange={(e) => setContent({ ...content, layout: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs">
                <option value="">Template default</option>
                {HERO_LAYOUTS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          <Button data-testid="section-save-btn" onClick={() => save.mutate()} disabled={save.isPending}
            className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">{save.isPending ? "Saving…" : "Save section"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function SortableSectionRow({ s, onToggle, onEdit }) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: s.section_type });
  const def = SECTION_DEFS.find((d) => d.type === s.section_type);
  return (
    <div ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}
      data-testid={`section-row-${s.section_type}`}
      className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
      <button {...attributes} {...listeners} data-testid={`drag-${s.section_type}`} className="cursor-grab text-slate-400 active:cursor-grabbing"><GripVertical size={15} /></button>
      <span className="flex-1 text-xs font-semibold text-[#0F2440]">{def?.label || s.section_type}</span>
      <button data-testid={`toggle-${s.section_type}`} onClick={() => onToggle(s)}
        className={`rounded-full px-2.5 py-1 text-[10px] font-bold ${s.is_visible !== false ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
        {s.is_visible !== false ? <><Eye size={10} className="mr-1 inline" />ON</> : <><EyeOff size={10} className="mr-1 inline" />OFF</>}
      </button>
      <Button size="sm" variant="outline" data-testid={`edit-${s.section_type}`} onClick={() => onEdit(s)} className="border-slate-200 text-slate-600"><Pencil size={11} /> Edit</Button>
    </div>
  );
}

// ---------- AI draft dialog (pages/branding/blog) ----------
function AIDraftDialog({ title, kind, extraFields, onClose, onUse }) {
  const [inputs, setInputs] = useState({});
  const [draft, setDraft] = useState("");
  const gen = useAiJob();
  useEffect(() => { if (gen.result?.draft) { setDraft(gen.result.draft); gen.clear(); } }, [gen.result]);
  useEffect(() => { if (gen.error) { toast.error(gen.error); gen.clear(); } }, [gen.error]);
  if (!kind) return null;
  return (
    <Dialog open={!!kind} onOpenChange={onClose}>
      <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
        <DialogHeader><DialogTitle>✨ Generate {title} with AI</DialogTitle></DialogHeader>
        {(extraFields || []).map(([k, label]) => (
          <div key={k}>
            <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
            <Input data-testid={`ai-input-${k}`} value={inputs[k] || ""} onChange={(e) => setInputs({ ...inputs, [k]: e.target.value })} className="border-slate-200 text-xs" />
          </div>
        ))}
        <Button data-testid="ai-generate-btn" onClick={() => gen.start("content", { type: kind, inputs }).catch((e) => toast.error(errMsg(e)))} disabled={gen.running}
          className="w-full bg-violet-600 font-semibold text-white hover:bg-violet-500">{gen.running ? "Generating… (up to a minute)" : draft ? "Regenerate" : "Generate"}</Button>
        {draft && (
          <>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-500">AI draft — review before using</p>
            <textarea data-testid="ai-draft-text" rows={10} value={draft} onChange={(e) => setDraft(e.target.value)}
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-violet-500" />
            <Button data-testid="ai-use-btn" onClick={() => { onUse(draft); onClose(); }} className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Use this content</Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main builder ----------
export default function StoreBuilder({ embedded = false }) {
  const qc = useQueryClient();
  const [editSection, setEditSection] = useState(null);
  const [aiFor, setAiFor] = useState(null); // {kind, pageKey}
  const [editPage, setEditPage] = useState(null);
  const [pageForm, setPageForm] = useState({});
  const [faqEdit, setFaqEdit] = useState(null);
  const [faqForm, setFaqForm] = useState({});
  const [faqAi, setFaqAi] = useState(false);
  const [faqAiOpts, setFaqAiOpts] = useState({ store_type: "", tone: "friendly and professional", count: 8 });
  const [faqDrafts, setFaqDrafts] = useState([]);
  const [blogEdit, setBlogEdit] = useState(null);
  const [blogForm, setBlogForm] = useState({});
  const [blogAiTopic, setBlogAiTopic] = useState("");
  const [brandForm, setBrandForm] = useState(null);
  const [navHeader, setNavHeader] = useState([]);
  const [navFooter, setNavFooter] = useState([]);
  const faviconRef = useRef();
  const logoRef = useRef();

  const { data: tplData } = useQuery({ queryKey: ["sb-templates"], queryFn: async () => (await api.get("/agent/store/templates")).data });
  const { data: sectionsData, refetch: refetchSections } = useQuery({ queryKey: ["sb-sections"], queryFn: async () => (await api.get("/agent/store/sections")).data });
  const { data: pagesData, refetch: refetchPages } = useQuery({ queryKey: ["sb-pages"], queryFn: async () => (await api.get("/agent/store/pages")).data });
  const { data: faqsData, refetch: refetchFaqs } = useQuery({ queryKey: ["sb-faqs"], queryFn: async () => (await api.get("/agent/store/faqs")).data });
  const { data: blogData, refetch: refetchBlog } = useQuery({ queryKey: ["sb-blog"], queryFn: async () => (await api.get("/agent/store/blog")).data });
  const { data: storeData } = useQuery({ queryKey: ["my-store"], queryFn: async () => (await api.get("/agent/store")).data });
  const { data: navData } = useQuery({ queryKey: ["sb-nav"], queryFn: async () => (await api.get("/agent/store/nav")).data });

  const store = storeData?.store;
  const storeUrl = store ? `${window.location.origin}/store/${store.slug}` : null;

  // merged sections: stored + defaults
  const sections = useMemo(() => {
    const stored = sectionsData?.items || [];
    const known = new Set(stored.map((s) => s.section_type));
    const merged = [...stored];
    SECTION_DEFS.forEach((d, i) => { if (!known.has(d.type)) merged.push({ section_type: d.type, is_visible: true, section_order: 99 + i, content: {} }); });
    return merged.sort((a, b) => (a.section_order ?? 99) - (b.section_order ?? 99));
  }, [sectionsData]);

  useEffect(() => {
    if (store && !brandForm) setBrandForm({ name: store.name, tagline: store.tagline || "", description: store.description || "", email: store.contact?.email || "", mobile: store.contact?.mobile || "", address: store.contact?.address || "", instagram: store.social?.instagram || "", facebook: store.social?.facebook || "", youtube: store.social?.youtube || "" });
  }, [store]);
  useEffect(() => { if (navData) {
    setNavHeader(navData.header?.length ? navData.header : [{ label: "Home", href: "" }, { label: "Shop", href: "shop" }, { label: "Track Order", href: "track" }, { label: "FAQ", href: "faq" }, { label: "Contact", href: "contact" }]);
    setNavFooter(navData.footer?.length ? navData.footer : [
      { group: "Shop", links: [{ label: "All Products", href: "shop" }, { label: "Track Order", href: "track" }] },
      { group: "Customer Care", links: [{ label: "My Account", href: "account" }, { label: "Contact", href: "contact" }, { label: "FAQ", href: "faq" }, { label: "Shipping", href: "shipping" }, { label: "Returns", href: "returns" }] },
      { group: "Company", links: [{ label: "About Us", href: "about" }, { label: "Blog", href: "blog" }] },
      { group: "Legal", links: [{ label: "Privacy Policy", href: "privacy" }, { label: "Terms", href: "terms" }, { label: "Refunds", href: "refunds" }, { label: "Cancellation", href: "cancellation" }] },
    ]);
  } }, [navData]);

  const saveTheme = useMutation({
    mutationFn: async (body) => api.put("/agent/store/theme", body),
    onSuccess: () => { toast.success("Theme saved"); qc.invalidateQueries({ queryKey: ["sb-templates"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleSection = async (s) => {
    try {
      await api.put(`/agent/store/sections/${s.section_type}`, { is_visible: s.is_visible === false });
      refetchSections();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const ids = sections.map((s) => s.section_type);
    const next = arrayMove(sections, ids.indexOf(active.id), ids.indexOf(over.id)).map((s) => s.section_type);
    try {
      await api.post("/agent/store/sections/reorder", { order: next });
      toast.success("Section order saved");
      refetchSections();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const savePage = useMutation({
    mutationFn: async () => api.put(`/agent/store/pages/${editPage}`, pageForm),
    onSuccess: () => { toast.success("Page saved"); setEditPage(null); refetchPages(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const togglePage = async (p) => {
    try {
      await api.put(`/agent/store/pages/${p.page_key}`, { is_visible: !p.is_visible });
      refetchPages();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveFaq = useMutation({
    mutationFn: async () => faqEdit?.id ? api.put(`/agent/store/faqs/${faqEdit.id}`, faqForm) : api.post("/agent/store/faqs", faqForm),
    onSuccess: () => { toast.success("FAQ saved"); setFaqEdit(null); refetchFaqs(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const deleteFaq = async (id) => { try { await api.delete(`/agent/store/faqs/${id}`); refetchFaqs(); } catch (e) { toast.error(errMsg(e)); } };
  const genFaqs = useAiJob();
  useEffect(() => {
    if (genFaqs.result?.drafts) {
      setFaqDrafts(genFaqs.result.drafts);
      if (!genFaqs.result.drafts.length) toast.error("AI returned nothing usable — try again");
      genFaqs.clear();
    }
  }, [genFaqs.result]);
  useEffect(() => { if (genFaqs.error) { toast.error(genFaqs.error); genFaqs.clear(); } }, [genFaqs.error]);
  const saveBlog = useMutation({
    mutationFn: async () => blogEdit?.id ? api.put(`/agent/store/blog/${blogEdit.id}`, blogForm) : api.post("/agent/store/blog", blogForm),
    onSuccess: () => { toast.success("Post saved"); setBlogEdit(null); refetchBlog(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const genBlog = useAiJob();
  useEffect(() => {
    if (genBlog.result?.draft) {
      setBlogForm((f) => ({ ...f, title: genBlog.result.draft.title, content: genBlog.result.draft.content }));
      toast.success("AI draft ready — review and save");
      genBlog.clear();
    }
  }, [genBlog.result]);
  useEffect(() => { if (genBlog.error) { toast.error(genBlog.error); genBlog.clear(); } }, [genBlog.error]);
  const saveBranding = useMutation({
    mutationFn: async () => api.put("/agent/store/branding", {
      name: brandForm.name, tagline: brandForm.tagline, description: brandForm.description,
      contact: { email: brandForm.email, mobile: brandForm.mobile, address: brandForm.address },
      social: { instagram: brandForm.instagram, facebook: brandForm.facebook, youtube: brandForm.youtube },
    }),
    onSuccess: () => { toast.success("Branding saved"); qc.invalidateQueries({ queryKey: ["my-store"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const uploadBrand = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(kind === "logo" ? "/agent/store/logo" : "/agent/store/branding/favicon", fd);
      toast.success(`${kind} uploaded`);
      qc.invalidateQueries({ queryKey: ["my-store"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const saveNav = useMutation({
    mutationFn: async () => api.put("/agent/store/nav", { header: navHeader, footer: navFooter }),
    onSuccess: () => toast.success("Navigation saved"),
    onError: (e) => toast.error(errMsg(e)),
  });

  if (!store) return <p className="text-sm text-slate-500">Set up your store first (Overview tab → Set up my store).</p>;

  return (
    <div data-testid="store-builder-page">
      {!embedded && (
      <PageHeader title="Store Builder" sub="Design your complete storefront — template, sections, images, pages, FAQs, blog and branding. Content stays safe when you switch templates." testid="store-builder-title">
        <Button data-testid="preview-store-btn" size="sm" variant="outline" onClick={() => window.open(storeUrl, "_blank")} className="border-slate-200 text-slate-600">
          <ExternalLink size={13} /> Live Preview
        </Button>
      </PageHeader>
      )}
      {embedded && (
        <div className="mb-3 flex justify-end">
          <Button data-testid="preview-store-btn" size="sm" variant="outline" onClick={() => window.open(storeUrl, "_blank")} className="border-slate-200 text-slate-600">
            <ExternalLink size={13} /> Live Preview
          </Button>
        </div>
      )}

      <Tabs defaultValue="templates">
        <TabsList className="flex-wrap border border-slate-200 bg-white">
          {[["templates", "Templates", LayoutTemplate], ["sections", "Sections", Layers], ["images", "Images", ImagePlus],
            ["pages", "Pages", FileText], ["faqs", "FAQs", MessageCircleQuestion], ["blog", "Blog", BookOpen],
            ["branding", "Branding", Palette], ["nav", "Navigation", Navigation]].map(([v, l, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`sb-tab-${v}`}><Icon size={12} className="mr-1" />{l}</TabsTrigger>
          ))}
        </TabsList>

        {/* TEMPLATES */}
        <TabsContent value="templates" className="mt-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="template-gallery">
            {TPL_META.map(([id, num, name, colors, desc]) => {
              const current = tplData?.current === id;
              const enabled = tplData?.templates?.find((t) => t.id === id)?.enabled !== false;
              return (
                <div key={id} data-testid={`template-card-${id}`} className={`overflow-hidden rounded-xl border-2 bg-white shadow-sm ${current ? "border-[#FF6B00]" : "border-slate-100"}`}>
                  <div className="flex h-28">
                    {colors.map((c) => <div key={c} className="flex-1" style={{ background: c }} />)}
                  </div>
                  <div className="p-4">
                    <p className="font-mono text-[10px] text-slate-400">TEMPLATE {num}</p>
                    <p className="text-sm font-bold text-[#0F2440]">{name} {current && <span className="ml-1 rounded-full bg-orange-50 px-2 py-0.5 text-[9px] font-bold text-[#E85D04]">CURRENT</span>}</p>
                    <p className="mt-0.5 text-[11px] text-slate-500">{desc}</p>
                    <div className="mt-3 flex gap-2">
                      <Button size="sm" data-testid={`use-template-${id}`} disabled={!enabled || current || saveTheme.isPending}
                        onClick={() => saveTheme.mutate({ template_id: id })}
                        className="bg-[#FF6B00] text-[11px] font-semibold text-white hover:bg-[#E85D04] disabled:opacity-40">
                        {current ? "In use" : enabled ? "Use this template" : "Disabled by admin"}
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`preview-template-${id}`} onClick={() => window.open(`${storeUrl}?tpl_preview=${id}`, "_blank")} className="border-slate-200 text-[11px] text-slate-600">Preview</Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="customize-card">
            <h3 className="mb-3 text-sm font-bold text-[#0F2440]">Colors & Fonts (overrides — template defaults stay intact)</h3>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {COLOR_KEYS.map(([k, label]) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                  <input data-testid={`color-${k}`} type="color" defaultValue="#FF6B00"
                    onChange={(e) => saveTheme.mutate({ colors: { [k]: e.target.value } })}
                    className="mt-1 h-9 w-full cursor-pointer rounded-lg border border-slate-200" />
                </div>
              ))}
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {["heading", "body", "button"].map((k) => (
                <div key={k}>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500">{k} font</p>
                  <select data-testid={`font-${k}`} onChange={(e) => e.target.value && saveTheme.mutate({ fonts: { [k]: `'${e.target.value}'` } })}
                    className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs">
                    <option value="">Template default</option>
                    {FONT_OPTIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" data-testid="reset-template-btn" onClick={() => saveTheme.mutate({ reset: true })}
              className="mt-4 border-slate-200 text-xs text-slate-600"><RotateCcw size={11} /> Reset to template defaults</Button>
          </div>
        </TabsContent>

        {/* SECTIONS */}
        <TabsContent value="sections" className="mt-4">
          <p className="mb-3 text-xs text-slate-500">Drag to reorder · toggle visibility · edit text and images. Changes are live on your store immediately.</p>
          <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={sections.map((s) => s.section_type)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2" data-testid="sections-list">
                {sections.map((s) => (
                  <SortableSectionRow key={s.section_type} s={s} onToggle={toggleSection} onEdit={setEditSection} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </TabsContent>

        {/* IMAGES */}
        <TabsContent value="images" className="mt-4">
          <p className="mb-3 text-xs text-slate-500">Your image library — upload or generate with AI, then pick them inside any section editor.</p>
          <ImagePicker sectionType="" current={null} onPick={() => {}} />
        </TabsContent>

        {/* PAGES */}
        <TabsContent value="pages" className="mt-4">
          <div className="space-y-2" data-testid="pages-list">
            {(pagesData?.items || []).map((p) => (
              <div key={p.page_key} data-testid={`page-row-${p.page_key}`} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                <span className="flex-1 text-xs font-semibold text-[#0F2440]">{p.title}</span>
                <span className="font-mono text-[10px] text-slate-400">/{p.page_key}</span>
                {p.content ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[9px] font-bold text-emerald-600">HAS CONTENT</span> : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-bold text-slate-400">EMPTY</span>}
                <button data-testid={`page-toggle-${p.page_key}`} onClick={() => togglePage(p)} disabled={!p.hideable && !p.is_visible}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-bold disabled:opacity-40 ${p.is_visible ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
                  {p.is_visible ? "LIVE" : "HIDDEN"}
                </button>
                <Button size="sm" variant="outline" data-testid={`page-edit-${p.page_key}`} className="border-slate-200 text-slate-600"
                  onClick={() => { setEditPage(p.page_key); setPageForm({ title: p.title, content: p.content, seo_title: p.seo_title, seo_description: p.seo_description }); }}>
                  <Pencil size={11} /> Edit
                </Button>
                {storeUrl && p.is_visible && !["home", "shop"].includes(p.page_key) && (
                  <a href={`${storeUrl}/${p.page_key}`} target="_blank" rel="noreferrer" className="text-[10px] font-semibold text-[#1D5BD8] hover:underline">View</a>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* FAQS */}
        <TabsContent value="faqs" className="mt-4">
          <div className="mb-3 flex gap-2">
            <Button size="sm" data-testid="add-faq-btn" onClick={() => { setFaqEdit({}); setFaqForm({ question: "", answer: "", category: "General", display_order: 99, is_visible: true }); }}
              className="bg-[#FF6B00] text-xs font-semibold text-white hover:bg-[#E85D04]">Add FAQ</Button>
            <Button size="sm" variant="outline" data-testid="ai-faq-btn" onClick={() => setFaqAi(true)} className="border-violet-300 text-xs text-violet-600"><Sparkles size={11} /> Generate FAQs with AI</Button>
          </div>
          {!faqsData?.items?.length ? <EmptyState title="No FAQs yet" sub="Add manually or generate a set with AI." /> : (
            <div className="space-y-2" data-testid="faqs-list">
              {faqsData.items.map((f) => (
                <div key={f.id} data-testid={`faq-row-${f.id}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-[#0F2440]">{f.question}</p>
                    <p className="text-[10px] text-slate-400">{f.category} · order {f.display_order}</p>
                  </div>
                  <StatusBadge status={f.is_visible ? "live" : "hidden"} />
                  <Button size="sm" variant="outline" data-testid={`faq-edit-${f.id}`} className="border-slate-200 text-slate-600"
                    onClick={() => { setFaqEdit(f); setFaqForm({ question: f.question, answer: f.answer, category: f.category, display_order: f.display_order, is_visible: f.is_visible }); }}><Pencil size={11} /></Button>
                  <Button size="sm" variant="outline" data-testid={`faq-del-${f.id}`} onClick={() => deleteFaq(f.id)} className="border-rose-200 text-rose-500"><Trash2 size={11} /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BLOG */}
        <TabsContent value="blog" className="mt-4">
          <div className="mb-3 flex gap-2">
            <Button size="sm" data-testid="new-post-btn" onClick={() => { setBlogEdit({}); setBlogForm({ title: "", content: "", status: "draft" }); setBlogAiTopic(""); }}
              className="bg-[#FF6B00] text-xs font-semibold text-white hover:bg-[#E85D04]">New post</Button>
          </div>
          {!blogData?.items?.length ? <EmptyState title="No posts yet" sub="Write your first post or let AI draft one." /> : (
            <div className="space-y-2" data-testid="blog-list">
              {blogData.items.map((b) => (
                <div key={b.id} data-testid={`blog-row-${b.id}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white px-4 py-3 shadow-sm">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-semibold text-[#0F2440]">{b.title}</p>
                    <p className="text-[10px] text-slate-400">{b.category} · {fmtDate(b.created_at)}</p>
                  </div>
                  <StatusBadge status={b.status} />
                  <Button size="sm" variant="outline" data-testid={`blog-edit-${b.id}`} className="border-slate-200 text-slate-600"
                    onClick={() => { setBlogEdit(b); setBlogForm({ ...b }); setBlogAiTopic(""); }}><Pencil size={11} /></Button>
                  <Button size="sm" variant="outline" data-testid={`blog-del-${b.id}`} className="border-rose-200 text-rose-500"
                    onClick={async () => { try { await api.delete(`/agent/store/blog/${b.id}`); refetchBlog(); } catch (e) { toast.error(errMsg(e)); } }}><Trash2 size={11} /></Button>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* BRANDING */}
        <TabsContent value="branding" className="mt-4">
          {brandForm && (
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="branding-card">
                <h3 className="mb-4 text-sm font-bold text-[#0F2440]">Identity</h3>
                <div className="mb-4 flex items-center gap-4">
                  <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-white">
                    {store.logo ? <img src={imgUrl(store.logo)} alt="" className="h-full w-full object-contain" /> : <ImagePlus size={18} className="text-slate-300" />}
                  </div>
                  <div className="flex flex-col gap-2">
                    <Button size="sm" variant="outline" data-testid="branding-logo-upload" onClick={() => logoRef.current.click()} className="border-slate-200 text-slate-600"><Upload size={11} /> Upload logo</Button>
                    <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadBrand(e.target.files[0], "logo")} />
                    <Button size="sm" variant="outline" data-testid="branding-favicon-upload" onClick={() => faviconRef.current.click()} className="border-slate-200 text-slate-600"><Upload size={11} /> Upload favicon</Button>
                    <input ref={faviconRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && uploadBrand(e.target.files[0], "favicon")} />
                  </div>
                  {store.favicon && <img src={imgUrl(store.favicon)} alt="favicon" className="h-8 w-8 rounded object-contain" data-testid="favicon-preview" />}
                </div>
                {[["name", "Store name"], ["tagline", "Tagline"], ["description", "Store description (SEO/social)"]].map(([k, label]) => (
                  <div key={k} className="mb-3">
                    <p className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-500">
                      {label}
                      {["tagline", "description"].includes(k) && (
                        <button data-testid={`ai-${k}`} className="flex items-center gap-1 text-violet-600 hover:underline" onClick={() => setAiFor({ kind: k, target: "brand" })}><Sparkles size={9} /> AI</button>
                      )}
                    </p>
                    <Input data-testid={`brand-${k}`} value={brandForm[k]} onChange={(e) => setBrandForm({ ...brandForm, [k]: e.target.value })} className="border-slate-200 text-xs" />
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm">
                <h3 className="mb-4 text-sm font-bold text-[#0F2440]">Contact & Social</h3>
                {[["email", "Contact email"], ["mobile", "Contact mobile"], ["address", "Business address"], ["instagram", "Instagram URL"], ["facebook", "Facebook URL"], ["youtube", "YouTube URL"]].map(([k, label]) => (
                  <div key={k} className="mb-3">
                    <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
                    <Input data-testid={`brand-${k}`} value={brandForm[k]} onChange={(e) => setBrandForm({ ...brandForm, [k]: e.target.value })} className="border-slate-200 text-xs" />
                  </div>
                ))}
              </div>
            </div>
          )}
          <Button data-testid="branding-save-btn" onClick={() => saveBranding.mutate()} disabled={saveBranding.isPending}
            className="mt-4 bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">{saveBranding.isPending ? "Saving…" : "Save branding"}</Button>
        </TabsContent>

        {/* NAV */}
        <TabsContent value="nav" className="mt-4">
          <div className="rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="nav-builder">
            <h3 className="mb-1 text-sm font-bold text-[#0F2440]">Header navigation</h3>
            <p className="mb-3 text-[11px] text-slate-500">Links are relative to your store (e.g. <b>shop</b>, <b>faq</b>, <b>category/Fashion</b>).</p>
            <div className="space-y-2">
              {navHeader.map((item, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`nav-item-${i}`}>
                  <Input value={item.label} onChange={(e) => setNavHeader(navHeader.map((x, j) => j === i ? { ...x, label: e.target.value } : x))} className="border-slate-200 text-xs" placeholder="Label" />
                  <Input value={item.href} onChange={(e) => setNavHeader(navHeader.map((x, j) => j === i ? { ...x, href: e.target.value } : x))} className="border-slate-200 text-xs" placeholder="href (e.g. shop)" />
                  <button data-testid={`nav-up-${i}`} disabled={i === 0} onClick={() => setNavHeader(arrayMove(navHeader, i, i - 1))} className="text-slate-400 disabled:opacity-30">↑</button>
                  <button data-testid={`nav-down-${i}`} disabled={i === navHeader.length - 1} onClick={() => setNavHeader(arrayMove(navHeader, i, i + 1))} className="text-slate-400 disabled:opacity-30">↓</button>
                  <button data-testid={`nav-del-${i}`} onClick={() => setNavHeader(navHeader.filter((_, j) => j !== i))} className="text-rose-400"><Trash2 size={12} /></button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" data-testid="nav-add-btn" onClick={() => setNavHeader([...navHeader, { label: "", href: "" }])} className="border-slate-200 text-slate-600">Add item</Button>
              <Button size="sm" data-testid="nav-save-btn" onClick={() => saveNav.mutate()} disabled={saveNav.isPending} className="bg-[#FF6B00] text-white hover:bg-[#E85D04]">Save navigation</Button>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-100 bg-white p-5 shadow-sm" data-testid="footer-builder">
            <h3 className="mb-1 text-sm font-bold text-[#0F2440]">Footer link groups</h3>
            <p className="mb-3 text-[11px] text-slate-500">Up to 6 groups, 10 links each. Links to hidden pages are auto-hidden on the storefront.</p>
            <div className="space-y-4">
              {navFooter.map((g, gi) => (
                <div key={gi} className="rounded-lg border border-slate-100 p-3" data-testid={`footer-group-${gi}`}>
                  <div className="mb-2 flex items-center gap-2">
                    <Input value={g.group} onChange={(e) => setNavFooter(navFooter.map((x, j) => j === gi ? { ...x, group: e.target.value } : x))}
                      className="border-slate-200 text-xs font-semibold" placeholder="Group name" data-testid={`footer-group-name-${gi}`} />
                    <button data-testid={`footer-group-del-${gi}`} onClick={() => setNavFooter(navFooter.filter((_, j) => j !== gi))} className="text-rose-400"><Trash2 size={12} /></button>
                  </div>
                  <div className="space-y-2">
                    {(g.links || []).map((l, li) => (
                      <div key={li} className="flex items-center gap-2" data-testid={`footer-link-row-${gi}-${li}`}>
                        <Input value={l.label} onChange={(e) => setNavFooter(navFooter.map((x, j) => j === gi ? { ...x, links: x.links.map((y, k) => k === li ? { ...y, label: e.target.value } : y) } : x))}
                          className="border-slate-200 text-xs" placeholder="Label" />
                        <Input value={l.href} onChange={(e) => setNavFooter(navFooter.map((x, j) => j === gi ? { ...x, links: x.links.map((y, k) => k === li ? { ...y, href: e.target.value } : y) } : x))}
                          className="border-slate-200 text-xs" placeholder="href (e.g. shipping)" />
                        <button data-testid={`footer-link-del-${gi}-${li}`} onClick={() => setNavFooter(navFooter.map((x, j) => j === gi ? { ...x, links: x.links.filter((_, k) => k !== li) } : x))}
                          className="text-rose-400"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                  <button data-testid={`footer-link-add-${gi}`} onClick={() => setNavFooter(navFooter.map((x, j) => j === gi ? { ...x, links: [...(x.links || []), { label: "", href: "" }] } : x))}
                    className="mt-2 text-[11px] font-semibold text-[#1D5BD8] hover:underline">+ Add link</button>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" data-testid="footer-group-add" disabled={navFooter.length >= 6}
                onClick={() => setNavFooter([...navFooter, { group: "", links: [] }])} className="border-slate-200 text-slate-600">Add group</Button>
              <Button size="sm" data-testid="footer-save-btn" onClick={() => saveNav.mutate()} disabled={saveNav.isPending} className="bg-[#FF6B00] text-white hover:bg-[#E85D04]">Save footer</Button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Section editor */}
      {editSection && <SectionEditor section={editSection} onClose={() => setEditSection(null)} onSaved={refetchSections} />}

      {/* Page editor */}
      <Dialog open={!!editPage} onOpenChange={() => setEditPage(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>Edit page — {editPage}</DialogTitle></DialogHeader>
          {["about", "contact", "shipping", "returns", "refunds", "privacy", "terms", "cancellation"].includes(editPage) && (
            <Button size="sm" variant="outline" data-testid="page-ai-btn" onClick={() => setAiFor({ kind: editPage === "refunds" ? "refunds" : editPage, target: "page" })}
              className="w-fit border-violet-300 text-violet-600"><Sparkles size={11} /> Generate with AI</Button>
          )}
          <div className="space-y-3">
            <div><p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Title</p>
              <Input data-testid="page-title-input" value={pageForm.title || ""} onChange={(e) => setPageForm({ ...pageForm, title: e.target.value })} className="border-slate-200 text-xs" /></div>
            <div><p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Content</p>
              <textarea data-testid="page-content-input" rows={10} value={pageForm.content || ""} onChange={(e) => setPageForm({ ...pageForm, content: e.target.value })}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-[#FF6B00]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">SEO title</p>
                <Input data-testid="page-seo-title" value={pageForm.seo_title || ""} onChange={(e) => setPageForm({ ...pageForm, seo_title: e.target.value })} className="border-slate-200 text-xs" /></div>
              <div><p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">SEO description</p>
                <Input data-testid="page-seo-desc" value={pageForm.seo_description || ""} onChange={(e) => setPageForm({ ...pageForm, seo_description: e.target.value })} className="border-slate-200 text-xs" /></div>
            </div>
            <Button data-testid="page-save-btn" onClick={() => savePage.mutate()} disabled={savePage.isPending}
              className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">{savePage.isPending ? "Saving…" : "Save page"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ editor */}
      <Dialog open={!!faqEdit} onOpenChange={() => setFaqEdit(null)}>
        <DialogContent className="border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>{faqEdit?.id ? "Edit FAQ" : "Add FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="faq-question" value={faqForm.question || ""} onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })} placeholder="Question" className="border-slate-200 text-xs" />
            <textarea data-testid="faq-answer" rows={4} value={faqForm.answer || ""} onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })} placeholder="Answer"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs outline-none focus:border-[#FF6B00]" />
            <div className="grid grid-cols-3 gap-3">
              <Input data-testid="faq-category" value={faqForm.category || ""} onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })} placeholder="Category" className="border-slate-200 text-xs" />
              <Input data-testid="faq-order" type="number" value={faqForm.display_order ?? 99} onChange={(e) => setFaqForm({ ...faqForm, display_order: Number(e.target.value) })} placeholder="Order" className="border-slate-200 text-xs" />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input data-testid="faq-visible" type="checkbox" checked={faqForm.is_visible !== false} onChange={(e) => setFaqForm({ ...faqForm, is_visible: e.target.checked })} /> Visible
              </label>
            </div>
            <Button data-testid="faq-save-btn" onClick={() => saveFaq.mutate()} disabled={saveFaq.isPending}
              className="w-full bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Save FAQ</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* FAQ AI */}
      <Dialog open={faqAi} onOpenChange={() => { setFaqAi(false); setFaqDrafts([]); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>✨ Generate FAQs with AI</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <Input data-testid="faqai-type" value={faqAiOpts.store_type} onChange={(e) => setFaqAiOpts({ ...faqAiOpts, store_type: e.target.value })} placeholder="Store type (e.g. fashion)" className="border-slate-200 text-xs" />
            <Input data-testid="faqai-tone" value={faqAiOpts.tone} onChange={(e) => setFaqAiOpts({ ...faqAiOpts, tone: e.target.value })} placeholder="Tone" className="border-slate-200 text-xs" />
            <Input data-testid="faqai-count" type="number" value={faqAiOpts.count} onChange={(e) => setFaqAiOpts({ ...faqAiOpts, count: Number(e.target.value) })} className="border-slate-200 text-xs" />
          </div>
          <Button data-testid="faqai-generate-btn" onClick={() => genFaqs.start("faqs", faqAiOpts).catch((e) => toast.error(errMsg(e)))} disabled={genFaqs.running}
            className="mt-3 w-full bg-violet-600 font-semibold text-white hover:bg-violet-500">{genFaqs.running ? "Generating… (up to a minute)" : "Generate"}</Button>
          {faqDrafts.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="faq-drafts">
              {faqDrafts.map((d, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-semibold">{d.question}</p>
                  <p className="mt-1 text-[11px] text-slate-500">{d.answer}</p>
                  <Button size="sm" data-testid={`faq-draft-use-${i}`} className="mt-2 bg-emerald-500 text-[10px] font-bold text-white hover:bg-emerald-600"
                    onClick={async () => { try { await api.post("/agent/store/faqs", d); toast.success("FAQ added"); refetchFaqs(); } catch (e) { toast.error(errMsg(e)); } }}>Add this FAQ</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Blog editor */}
      <Dialog open={!!blogEdit} onOpenChange={() => setBlogEdit(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-slate-200 bg-white text-[#0F2440]">
          <DialogHeader><DialogTitle>{blogEdit?.id ? "Edit post" : "New post"}</DialogTitle></DialogHeader>
          <div className="mb-3 flex gap-2">
            <Input data-testid="blog-ai-topic" value={blogAiTopic} onChange={(e) => setBlogAiTopic(e.target.value)} placeholder="AI topic (e.g. monsoon home care tips)" className="border-slate-200 text-xs" />
            <Button size="sm" data-testid="blog-ai-btn" onClick={() => genBlog.start("blog", { topic: blogAiTopic, tone: "friendly", length: "medium", audience: "Indian shoppers" }).catch((e) => toast.error(errMsg(e)))} disabled={genBlog.running || !blogAiTopic}
              className="shrink-0 bg-violet-600 text-white hover:bg-violet-500"><Sparkles size={11} /> {genBlog.running ? "Generating…" : "AI draft"}</Button>
          </div>
          <div className="space-y-3">
            <Input data-testid="blog-title" value={blogForm.title || ""} onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })} placeholder="Title" className="border-slate-200 text-xs" />
            <div>
              <p className="mb-1 text-[10px] uppercase tracking-wider text-slate-500">Cover image</p>
              <ImagePicker sectionType="blog" current={blogForm.cover_image} onPick={(url) => setBlogForm({ ...blogForm, cover_image: url })} />
            </div>
            <textarea data-testid="blog-content" rows={8} value={blogForm.content || ""} onChange={(e) => setBlogForm({ ...blogForm, content: e.target.value })} placeholder="Write your post…"
              className="w-full rounded-lg border border-slate-200 px-3 py-2 text-xs leading-relaxed outline-none focus:border-[#FF6B00]" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="blog-category" value={blogForm.category || ""} onChange={(e) => setBlogForm({ ...blogForm, category: e.target.value })} placeholder="Category" className="border-slate-200 text-xs" />
              <Input data-testid="blog-author" value={blogForm.author || ""} onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })} placeholder="Author" className="border-slate-200 text-xs" />
              <Input data-testid="blog-seo-title" value={blogForm.seo_title || ""} onChange={(e) => setBlogForm({ ...blogForm, seo_title: e.target.value })} placeholder="SEO title" className="border-slate-200 text-xs" />
              <Input data-testid="blog-seo-desc" value={blogForm.seo_description || ""} onChange={(e) => setBlogForm({ ...blogForm, seo_description: e.target.value })} placeholder="SEO description" className="border-slate-200 text-xs" />
            </div>
            <div className="flex gap-2">
              <Button data-testid="blog-save-draft" variant="outline" onClick={() => { setBlogForm((f) => ({ ...f, status: "draft" })); setTimeout(() => saveBlog.mutate(), 0); }}
                className="flex-1 border-slate-200 text-slate-600">Save draft</Button>
              <Button data-testid="blog-publish-btn" onClick={() => { setBlogForm((f) => ({ ...f, status: "published" })); setTimeout(() => saveBlog.mutate(), 0); }}
                className="flex-1 bg-[#FF6B00] font-semibold text-white hover:bg-[#E85D04]">Publish</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI content for pages/branding */}
      {aiFor && (
        <AIDraftDialog title={aiFor.kind} kind={aiFor.kind}
          extraFields={[["details", "Details / facts to include (store type, delivery info, return window…)"]]}
          onClose={() => setAiFor(null)}
          onUse={(draft) => {
            if (aiFor.target === "page") setPageForm((f) => ({ ...f, content: draft }));
            else setBrandForm((f) => ({ ...f, [aiFor.kind]: draft }));
            toast.success("Draft inserted — review and save");
          }} />
      )}
    </div>
  );
}
